import { Modal as ModalModule, Terms, UI, Utils } from 'common';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { LogFields, PrefValues } from './CommPrefs.enum';
import SmsTermsModal from './SmsTermsModal.component';

const { WrappedCheckbox, WrappedButton, SaveConfirmation } = UI;
const { Session, Logger, UserRole } = Utils;
const { ModalContext } = ModalModule;
const { TermsDialog } = Terms;

export class CommPrefs extends Component {
	constructor(props) {
		super(props);

		Session.setFormStateDirty(false);

		this.state = {
			stampsOffers: 0,
			monthlyNewsletter: 0,
			partnerOffers: 0,
			autoDial: 0,
			sendMarketingSms: 0,
			initialPrefs: {},
			saved: false,
			clean: true,
		};
	}

	async componentDidMount() {
		const {
			stampsOffers,
			monthlyNewsletter,
			partnerOffers,
			autoDial,
			sendMarketingSms,
		} = this.state;

		const { showError } = this.context;
		const response = await AccountCommunication.getCommPrefs();

		if (!AccountCommunication.isSuccessful(response)) {
			return showError({ response });
		}

		const initialPrefs = {
			stampsOffers,
			monthlyNewsletter,
			partnerOffers,
			autoDial,
			sendMarketingSms,
		};

		// the following loop performs bit math to check which preference fields have been selected
		Object.entries(PrefValues).forEach(entry => {
			const [key] = entry;
			const selectedPreference = response.data & PrefValues[key];

			if (selectedPreference) {
				initialPrefs[key] = PrefValues[key];
			}
		});

		// set response data to state and save initial preferences for logs
		this.setState({
			...initialPrefs,
			initialPrefs,
		});
	}

	onClickCheckbox = e => {
		const { name, value } = e.target;

		// set field values to their mapped values when selected
		this.setState({
			[name]: value === 0 ? PrefValues[name] : 0,
			saved: false,
			clean: false,
		});

		Session.setFormStateDirty(true);
	};

	onSaveButtonClick = async () => {
		const { showError } = this.context;
		const {
			stampsOffers,
			monthlyNewsletter,
			partnerOffers,
			autoDial,
			sendMarketingSms,
		} = this.state;

		const data = {
			UserCommunicationPreference:
				stampsOffers +
				monthlyNewsletter +
				partnerOffers +
				autoDial +
				sendMarketingSms,
		};

		Logger.saveActionData('Save Communication Preferences Button Clicked');

		const response = await AccountCommunication.setCommPrefs(data);

		if (AccountCommunication.isSuccessful(response)) {
			const resetPrefs = {
				stampsOffers,
				monthlyNewsletter,
				partnerOffers,
				autoDial,
				sendMarketingSms,
			};
			const fieldsChanged = this.getChanges();
			const userRole = Session.getUserRole();

			this.setState({
				initialPrefs: resetPrefs,
				saved: true,
				clean: true,
			});

			Session.setFormStateDirty(false);

			// TODO: contactType
			if (fieldsChanged.length > 0) {
				Logger.logToServer({
					category: 'Save Changes',
					contactType:
						userRole === UserRole.CorpAdmin ? 'corporate' : 'location',
					fieldsChanged,
				});
			}
		} else {
			this.setState({ clean: false });

			showError({ response });
		}
	};

	// return an array of changed fields, with their names adjusted for splunk logs
	getChanges = () => {
		const { initialPrefs } = this.state;

		const fieldsChanged = Object.keys(initialPrefs)
			.filter(key => {
				const { [key]: prefValue } = this.state;

				return initialPrefs[key] !== prefValue;
			})
			.map(key => LogFields[key]);

		return fieldsChanged;
	};

	launchPrivacyPolicyModal = () => {
		const { showModal } = this.context;

		showModal({
			component: TermsDialog,
			data: {
				termType: 'Privacy',
			},
		});
	};

	launchSmsTermsModal = () => {
		const { showModal } = this.context;

		showModal({
			component: SmsTermsModal,
		});
	};

	render() {
		const {
			stampsOffers,
			monthlyNewsletter,
			partnerOffers,
			autoDial,
			sendMarketingSms,
			saved,
			clean,
		} = this.state;

		return (
			<div className="account-comm-prefs">
				<div className="account-title">
					Communication Preferences
					{saved && <SaveConfirmation success text="Changes Saved" />}
				</div>
				<div className="comm-prefs-body">
					<p className="header-text">
						<b>
							We want to stay in touch, but only in ways that you find helpful.
						</b>
					</p>
					<div className="preference-checks">
						<WrappedCheckbox
							inputId="cb1"
							name="stampsOffers"
							value={stampsOffers}
							onChange={this.onClickCheckbox}
							checked={stampsOffers !== 0}
							htmlFor="cb1"
							dataqa="stampsoffers"
						>
							Send me special money-saving offers for Stamps.com products.
						</WrappedCheckbox>
						<WrappedCheckbox
							inputId="cb2"
							name="monthlyNewsletter"
							value={monthlyNewsletter}
							onChange={this.onClickCheckbox}
							checked={monthlyNewsletter !== 0}
							htmlFor="cb2"
							dataqa="monthlynewsletter"
						>
							Send me a monthly newsletter highlighting industry news and new
							products, features, and services.
						</WrappedCheckbox>
						<WrappedCheckbox
							inputId="cb3"
							name="partnerOffers"
							value={partnerOffers}
							onChange={this.onClickCheckbox}
							checked={partnerOffers !== 0}
							htmlFor="cb3"
							dataqa="partneroffers"
						>
							Send me special offers from Stamps.com partners.
						</WrappedCheckbox>
						<WrappedCheckbox
							inputId="cb4"
							name="sendMarketingSms"
							value={sendMarketingSms}
							onChange={this.onClickCheckbox}
							checked={sendMarketingSms !== 0}
							htmlFor="cb4"
							dataqa="sendMarketingSms"
						>
							<p>
								I agree to the{' '}
								<a
									role="button"
									tabIndex={sendMarketingSms}
									onClick={this.launchSmsTermsModal}
								>
									SMS Terms
								</a>{' '}
								and to receive SMS messages from or on behalf of Stamps.com.
							</p>
						</WrappedCheckbox>
						<WrappedCheckbox
							inputId="cb5"
							name="autoDial"
							value={autoDial}
							onChange={this.onClickCheckbox}
							checked={autoDial !== 0}
							htmlFor="cb5"
							dataqa="autodial"
						>
							I give my consent to receive autodialed calls from or on behalf of{' '}
							Stamps.com to contact me at the number provided for technical
							support, product updates and general account support. I understand
							that I can revoke this consent at any time by changing the
							settings in my account and that consent is not a condition to
							purchase.
						</WrappedCheckbox>
					</div>
					<div className="footer">
						<p className="footer-text">
							Stamps.com values its customers and respects their privacy. Please
							read our{' '}
							<a
								rel="noopener noreferrer"
								target="_blank"
								href={`https://content.stamps.com/ContentService/conditions/PrivacyPolicy`}
							>
								Privacy Policy
							</a>{' '}
							for more information.
						</p>
						<WrappedButton
							label="Save"
							onClick={this.onSaveButtonClick}
							disabled={clean}
							dataqa="savecommprefs"
						/>
					</div>
				</div>
			</div>
		);
	}
}

CommPrefs.contextType = ModalContext;
